import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
} from "@mui/material";
import {
  LocationOn as MapPin,
  Flight as Plane,
  Hotel,
  Wifi,
} from "@mui/icons-material";

export default function VenueSection() {
  const venueFeatures = [
    {
      img: "/images/spacepattaya_ballroom.jpg",
      title: "Space Convention Centre",
      text: "A pillarless ballroom with 8-metre ceiling, LED system, and flexible layouts for up to 1,000 participants.",
    },
    {
      img: "/images/spacepattaya_meeting.jpg",
      title: "Meeting & Breakout Rooms",
      text: "Seven well-equipped rooms for technical sessions, workshops, and poster discussions.",
    },
    {
      img: "/images/spacepattaya_sky.jpg",
      title: "Sky Lounge & Terrace",
      text: "Enjoy networking receptions with ocean views and sunset backdrops.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: 8,
        background: "linear-gradient(to bottom, #f8fafc, #ffffff)",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #2c5f7c 0%, #5fb3d0 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              mb: 1,
              fontSize: {
                xs: "1.75rem",
                sm: "2.25rem",
                md: "3rem",
              },
            }}
          >
            Venue — ISAP 2027
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: {
                xs: "0.875rem",
                sm: "1rem",
              },
            }}
          >
            Grande Centre Point Space Pattaya, Thailand
          </Typography>
        </Box>

        {/* Main Venue Info */}
        <Grid container spacing={4} alignItems="center" sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/images/spacepattaya_main.jpg"
              alt="Grande Centre Point Space Pattaya exterior"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 4,
                boxShadow: "0 10px 40px rgba(44, 95, 124, 0.15)",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                lineHeight: 1.7,
                mb: 2,
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                },
              }}
            >
              ISAP 2027 will be held at{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
                Grande Centre Point Space Pattaya
              </Box>{" "}
              — Thailand's first space-themed five-star hotel and convention
              complex, located along the beautiful Pattaya coastline.
            </Typography>

            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              {[
                {
                  icon: <MapPin sx={{ fontSize: 20 }} />,
                  text: "888 Moo 5, Na Kluea, Bang Lamung, Chon Buri 20150, Thailand",
                },
                {
                  icon: <Plane sx={{ fontSize: 20 }} />,
                  text: "1.5 hrs from Suvarnabhumi Airport / 45 mins from U-Tapao Airport",
                },
                {
                  icon: <Hotel sx={{ fontSize: 20 }} />,
                  text: "490+ rooms with modern space-inspired design",
                },
                {
                  icon: <Wifi sx={{ fontSize: 20 }} />,
                  text: "High-speed Wi-Fi and full conference facilities",
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  component="li"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1.5,
                    color: "text.secondary",
                    fontSize: {
                      xs: "0.875rem",
                      sm: "1rem",
                    },
                  }}
                >
                  <Box sx={{ color: "primary.main", mt: 0.5 }}>{item.icon}</Box>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Venue Features */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {venueFeatures.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(44, 95, 124, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 8px 24px rgba(44, 95, 124, 0.15)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Box
                  component="img"
                  src={item.img}
                  alt={item.title}
                  sx={{
                    width: "100%",
                    height: 192,
                    objectFit: "cover",
                  }}
                />
                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      color: "primary.main",
                      mb: 1,
                      fontSize: {
                        xs: "1rem",
                        sm: "1.125rem",
                      },
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.6,
                      fontSize: {
                        xs: "0.8125rem",
                        sm: "0.875rem",
                      },
                    }}
                  >
                    {item.text}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Website Link */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 1.5,
              fontSize: {
                xs: "0.875rem",
                sm: "1rem",
              },
            }}
          >
            Visit the official venue website:
          </Typography>
          <Button
            component="a"
            href="https://spacepattaya.com"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            sx={{
              px: 3,
              py: 1.5,
              backgroundColor: "primary.main",
              color: "white",
              borderRadius: 3,
              textTransform: "none",
              fontSize: {
                xs: "0.875rem",
                sm: "1rem",
              },
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(44, 95, 124, 0.3)",
              "&:hover": {
                backgroundColor: "primary.dark",
                boxShadow: "0 6px 16px rgba(44, 95, 124, 0.4)",
              },
            }}
          >
            spacepattaya.com
          </Button>
        </Box>

        {/* Accommodation & Transportation Section */}
        <Box
          sx={{
            mt: 12,
            pt: 8,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h4"
            component="h3"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              textAlign: "center",
              mb: 4,
              fontSize: {
                xs: "1.5rem",
                sm: "1.75rem",
                md: "2rem",
              },
            }}
          >
            Accommodation & Transportation
          </Typography>

          <Grid container spacing={5} sx={{ mb: 6 }}>
            {/* Accommodation */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h5"
                component="h4"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 2,
                  fontSize: {
                    xs: "1.125rem",
                    sm: "1.25rem",
                  },
                }}
              >
                🏨 Accommodation
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.7,
                  mb: 2,
                  fontSize: {
                    xs: "0.875rem",
                    sm: "1rem",
                  },
                }}
              >
                Participants are encouraged to stay at{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Grande Centre Point Space Pattaya
                </Box>
                , where all conference sessions will take place. The hotel offers
                over 490 rooms and suites featuring modern interiors with a
                cosmic touch, providing comfort and convenience for delegates.
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 3,
                  color: "text.secondary",
                  "& li": {
                    mb: 0.5,
                    fontSize: {
                      xs: "0.875rem",
                      sm: "1rem",
                    },
                  },
                }}
              >
                <li>Special ISAP2027 room rates for registered participants</li>
                <li>Breakfast and Wi-Fi included</li>
                <li>Sky lounge, restaurants, and relaxation areas on-site</li>
                <li>Walking distance to Terminal 21 Pattaya and beachfront</li>
              </Box>
            </Grid>

            {/* Transportation */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h5"
                component="h4"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 2,
                  fontSize: {
                    xs: "1.125rem",
                    sm: "1.25rem",
                  },
                }}
              >
                ✈️ Transportation
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.7,
                  mb: 2,
                  fontSize: {
                    xs: "0.875rem",
                    sm: "1rem",
                  },
                }}
              >
                Pattaya is easily accessible from Bangkok and nearby airports via
                expressway. Shuttle services will be arranged during the
                conference period.
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 3,
                  color: "text.secondary",
                  "& li": {
                    mb: 0.5,
                    fontSize: {
                      xs: "0.875rem",
                      sm: "1rem",
                    },
                  },
                  "& strong": {
                    fontWeight: 600,
                    color: "text.primary",
                  },
                }}
              >
                <li>
                  <strong>From Suvarnabhumi Airport (BKK):</strong> ~1.5 hours by
                  motorway (E7)
                </li>
                <li>
                  <strong>From U-Tapao Rayong–Pattaya Airport (UTP):</strong> ~45
                  mins
                </li>
                <li>
                  <strong>From Don Mueang Airport (DMK):</strong> ~2 hours via
                  expressway
                </li>
                <li>
                  Private transfers and shared shuttle buses available for
                  delegates
                </li>
              </Box>
            </Grid>
          </Grid>

          {/* Google Maps */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: 900,
                height: {
                  xs: 300,
                  sm: 400,
                  md: 450,
                },
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 40px rgba(44, 95, 124, 0.15)",
              }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3884.3946947563877!2d100.88590931533624!3d12.953047318399945!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3102bd123bc7dc3b%3A0xa25df784e58eea2!2sGrande%20Centre%20Point%20Space%20Pattaya!5e0!3m2!1sen!2sth!4v1698394800000!5m2!1sen!2sth"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Grande Centre Point Space Pattaya Location"
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
